import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import MetaTags from "../../components/SEO/MetaTags";
import { pageConfigs } from "../../utils/seo";

export default function DocumentationPhysicalTherapy() {
  return (
    <>
      <MetaTags
        title={pageConfigs.blogDocumentationPT.title}
        description={pageConfigs.blogDocumentationPT.description}
        keywords={pageConfigs.blogDocumentationPT.keywords}
        canonical={pageConfigs.blogDocumentationPT.canonical}
      />
      <div className="bg-white">
      {/* Hero section with title */}
      <div className="bg-darkGreen text-white pt-36 pb-8 mb-16 relative overflow-hidden">
        <div className="relative z-10">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h4 className="text-emerald-400 font-medium mb-2">Blog Post</h4>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4 font-productSans">
              The Power of Documentation in Physical Therapy
            </h1>
            <p className="text-emerald-100 text-lg">
              Reimagining Patient Progress & Clinic Efficiency
            </p>
            <p className="text-emerald-200 text-sm mt-4">
              By Christina Rama PT, DPT • June 6, 2025
            </p>
          </div>
        </div>
      </div>

      {/* Blog content */}
      <div className="max-w-3xl mx-auto px-4 pb-16">
        {/* Back button at top */}
        <div className="mb-8">
          <Link
            to="/blogs"
            className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blogs
          </Link>
        </div>

        {/* Blog image */}
        <div className="mb-12">
          <img
            src="/blog/blog-1.png"
            alt="Power of Documentation in Physical Therapy"
            className="w-full rounded-lg shadow-lg"
          />
        </div>

        <div className="prose prose-lg">
          <p className="lead">
            In physical therapy, effective documentation is much more than a compliance necessity—it's a powerful tool that impacts patient outcomes, clinic efficiency, and even the growth of a practice. Here's why documentation holds the key to success in physical therapy:
          </p>

          <h2>1. Enhancing Patient Progress</h2>
          <p>
            Documentation provides a clear and measurable record of a patient's journey. By tracking improvements in range of motion, strength, and pain levels, therapists can fine-tune treatment plans for optimal outcomes.
          </p>

          <h2>2. Promoting Continuity of Care</h2>
          <p>
            In a busy clinic, multiple therapists may work with the same patient. Detailed notes ensure everyone is aligned, creating a seamless experience for the patient and a more effective treatment journey.
          </p>

          <h2>3. Supporting Compliance and Reimbursement</h2>
          <p>
            Insurance companies and regulatory bodies require accurate records to validate the medical necessity of treatment. Well-documented notes ensure clinics stay compliant and secure reimbursements without delays.
          </p>

          <h2>4. Boosting Patient Engagement</h2>
          <p>
            Sharing documented progress with patients fosters trust and motivation. When patients see tangible evidence of their improvement, they're more likely to stay committed to their treatment plan.
          </p>

          <div className="my-8">
            <p className="text-xl font-semibold">
              Documentation is a powerful tool that drives success in physical therapy. It enables better patient outcomes, streamlined care, and operational efficiency. With innovative solutions like FOWND's Notation platform, therapists can simplify the process, save time, and devote their energy to delivering the highest quality of care.
            </p>
          </div>

          {/* Try Notation CTA Banner */}
          <div className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] rounded-2xl p-8 text-center my-12">
            <p className="text-white text-lg font-semibold mb-6">
              👉 Try Notation for your next evaluation — no strings attached.
            </p>
            <a
              href="https://notation.fownd.care/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-[#619ed3] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Try Notation
            </a>
          </div>
        </div>

        {/* Back button at bottom */}
        <div className="mt-12">
          <Link
            to="/blogs"
            className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blogs
          </Link>
        </div>
      </div>
      </div>
    </>
  );
} 